import { useAuth0 } from '@auth0/auth0-react';
import { useMutation } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import { useEffect, useMemo } from 'react';

/**
 * User information from Auth0
 */
export interface AuthUser {
  id: string;
  email: string;
  name: string;
  picture?: string;
  isAuthenticated: boolean;
}

/**
 * Hook that provides authenticated user information for use with Convex
 */
export function useAuthUser(): AuthUser | null {
  const { isAuthenticated, user, isLoading } = useAuth0();

  return useMemo(() => {
    if (isLoading || !isAuthenticated || !user) {
      return null;
    }

    const email = (user.email || '').toLowerCase().trim();

    if (!user.sub) {
      return null;
    }

    return {
      id: user.sub,
      email,
      name: user.name || user.email || 'User',
      picture: user.picture,
      isAuthenticated: true,
    };
  }, [isAuthenticated, user, isLoading]);
}

/**
 * Get user ID for Convex queries/mutations.
 * Returns null if the user is not authenticated or if Auth0 sub is absent.
 */
export function useUserId(): string | null {
  const authUser = useAuthUser();
  return authUser?.id ?? null;
}

/**
 * Get user ID, throwing if the user is unauthenticated.
 */
export function useRequiredUserId(): string {
  const id = useUserId();
  if (!id) {
    throw new Error("useRequiredUserId: user is not authenticated");
  }
  return id;
}

/**
 * Self-provision the current Auth0 user into the Convex users/tenants tables.
 * Idempotent: safe to run on every authenticated session.
 */
export function useEnsureUser(): { isLoading: boolean; error: Error | null } {
  const { isAuthenticated, isLoading: authLoading } = useAuth0();
  const ensure = useMutation(api.userProvisioning.ensureCurrentUser);

  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      ensure({}).catch(() => {
        // Swallow: the next authenticated request will retry provisioning.
      });
    }
  }, [isAuthenticated, authLoading, ensure]);

  return { isLoading: authLoading, error: null };
}
